// cleanup_convert_to_typescript_file_SECURITY_CONTACT

import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { template } from 'lodash';

import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';
import { Input } from '@glu/form-components';

import PhoneNumber from 'components/phoneNumber/PhoneNumber';
import emailAddress from 'utils/validations/emailAddress';
import phoneNumber from 'utils/validations/phoneNumber';
import { SecurityContact } from 'proptypes/user';
import { sendActivationCode } from 'api/user/service';
import useLocale from 'components/useLocale';
import { DEFAULT_PROPS } from 'constants.js';

import useStyles from './SecurityContactField.styles';
import {
  contactTypes,
  INPUT_TYPES,
  SMS_NUMBER_MAX,
  VOICE_CALL_NUMBER_FORMAT
} from './securityContacts.constants';
import reducer, { ACTIONS } from './SecurityContactField.reducer';

const initialState = {
  activationCode: '',
  sending: false
};

const { CANCEL, HANDLE_INPUT_CHANGE, SET_SENDING } = ACTIONS;
const { EMPTY_FUNCTION, EMPTY_OBJ } = DEFAULT_PROPS;

const SecurityFieldComponent = ({
  contact = EMPTY_OBJ,
  contact: {
    contactType,
    errorMessage,
    extension = '',
    inputType,
    name,
    sent,
    value = '',
    verified,
    vruDelay
  } = EMPTY_OBJ,
  disabled = false,
  format = '($1) $2-$3',
  handleError: handleErrorFromProps,
  onChange = EMPTY_FUNCTION,
  onReset = EMPTY_FUNCTION,
  resetError
}) => {
  const locale = useLocale();
  const { btnLink, inputsGroup } = useStyles();
  const [{ numberText, sending }, dispatch] = useReducer(reducer, {
    ...initialState,
    numberText: contact?.value || ''
  });
  const staticValue = value.replace(/(\d{3})(\d{3})(\d{4})/i, format);
  const showActivation =
    contactType === contactTypes.sms &&
    !errorMessage &&
    value &&
    !verified &&
    !sent &&
    value.length >= 10;
  const maxLength = contactType === contactTypes.sms ? SMS_NUMBER_MAX : '';

  const change = (changeValue) => {
    onChange({ ...contact, ...changeValue });
  };

  const handleInputChange = ({ extension, number, target = {}, value: numberText, vruDelay }) => {
    const value = number || target.value || '';

    const changeValue = {
      extension,
      numberText,
      sent: false,
      value: value.replace(/\s/g, ''),
      verified: false,
      vruDelay
    };

    change(changeValue);
    dispatch({ type: HANDLE_INPUT_CHANGE, payload: changeValue });
  };

  const validations = () => {
    if (inputType === INPUT_TYPES.PHONE) {
      return change({
        errorMessage:
          value &&
          phoneNumber(value, { locale, skipLengthValidation: contactType === contactTypes.voice })
      });
    }
    if (inputType === INPUT_TYPES.EMAIL) {
      return change({ errorMessage: value && emailAddress(value, { locale }) });
    }
    return null;
  };

  const remove = () => {
    handleInputChange({ extension: '', number: '', value: '', vruDelay: '' });
  };

  const cancel = () => {
    dispatch({
      type: CANCEL,
      payload: {
        errorMesage: '',
        numberText: '',
        successMessage: ''
      }
    });

    return onReset(contact);
  };

  const handleError = (data) => {
    handleErrorFromProps(data?.data);
  };

  const handleResponse = (data) => {
    const { success: sent } = data;
    dispatch({ type: SET_SENDING, payload: false });
    if (!sent) {
      return handleError(data);
    }
    return change({ errorMessage: '', sent });
  };

  const sendCode = () => {
    resetError();
    dispatch({ type: SET_SENDING, payload: true });

    sendActivationCode({
      contactType,
      default: false,
      value
    })
      .then(handleResponse)
      .catch(handleError);
  };

  if (verified && value) {
    return (
      <p data-qa={`${contactType}-saved-value`}>
        {staticValue}
        {extension ? `, ${extension}` : ''}
        <br />
        {vruDelay
          ? `${locale['common.delay'].toLowerCase()} : ${template(
              locale['common.time.template.seconds']
            )({ seconds: vruDelay })}`
          : ''}
        <br />
        <Button
          className="btn btn-link btn-tertiary"
          dataQa={`${contactType}-remove`}
          disabled={disabled}
          onClick={remove}
          text={locale['common.remove']}
          type="button"
          variant={TERTIARY}
        />
      </p>
    );
  }

  return (
    <div className={classnames({ [inputsGroup]: showActivation })}>
      {inputType === INPUT_TYPES.PHONE && (
        <PhoneNumber
          autoComplete="off"
          data-qa={`${contactType}-input`}
          disabled={disabled}
          format={contactType === contactTypes.sms ? undefined : VOICE_CALL_NUMBER_FORMAT}
          id={name}
          maxLength={maxLength}
          name={name}
          onBlur={validations}
          onChange={handleInputChange}
          value={value && numberText}
        />
      )}
      {inputType === INPUT_TYPES.EMAIL && (
        <Input
          autoComplete="off"
          data-qa={`${contactType}-input`}
          disabled={disabled}
          id={name}
          maxLength="255"
          name={name}
          onBlur={validations}
          onChange={handleInputChange}
          type="text"
          value={value}
        />
      )}

      {showActivation && (
        <>
          <Button
            className="btn btn-primary"
            dataQa="send-activation-code-button"
            disabled={disabled || sending}
            onClick={sendCode}
            text={locale['profile.activationCode']}
            variant={PRIMARY}
          />
          <Button
            className={classnames('btn', 'btn-link', btnLink)}
            disabled={disabled}
            onClick={cancel}
            text={locale['common.cancel']}
            variant={TERTIARY}
          />
        </>
      )}
    </div>
  );
};

SecurityFieldComponent.propTypes = {
  contact: SecurityContact,
  disabled: PropTypes.bool,
  format: PropTypes.string,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  resetError: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired
};

export default SecurityFieldComponent;
